import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../api';
import { EyeOff, Eye } from 'react-feather';
import { FaGoogle, FaApple } from 'react-icons/fa';

export default function Register() {
  
  const navigate = useNavigate();
  const { login } = useAuth();
  
  // 1. Nuevo estado para el Checkbox
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    dni: '',
    email: '',
  });
  
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    // 2. Validación de Términos antes de enviar nada
    if (!acceptedTerms) {
        setError('Debes aceptar los Términos y Condiciones para continuar.');
        return;
    }

    const cleanFormData = {
      ...formData,
      name: String(formData.name || '').trim(),
      dni: String(formData.dni || '').trim(),
    };

    try {
      const response = await fetch(`${API_URL}/users/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cleanFormData),
      });

      if (!response.ok) {
        let data;
        try {
          data = await response.json();
        } catch (err) {
          throw new Error('Registration failed');
        }
        
        if (data.detail) {
          if (typeof data.detail === 'string') {
            throw new Error(data.detail);
          } else if (Array.isArray(data.detail)) {
            const fieldErrs = {};
            data.detail.forEach((err) => {
              if (err.loc && err.loc.length > 0) {
                const field = err.loc[err.loc.length - 1];
                fieldErrs[field] = err.msg;
              }
            });
            setFieldErrors(fieldErrs);
            throw new Error('Revisa los campos marcados en rojo.');
          } else if (typeof data.detail === 'object') {
            const fieldErrs = {};
            Object.entries(data.detail).forEach(([field, msg]) => {
              fieldErrs[field] = Array.isArray(msg) ? msg.join(', ') : msg;
            });
            setFieldErrors(fieldErrs);
            throw new Error('Revisa los campos marcados en rojo.');
          }
        }
        throw new Error('Registration failed');
      }

      // --- Auto Login Logic ---
      const loginPayload = new URLSearchParams();
      loginPayload.append('username', cleanFormData.username);
      loginPayload.append('password', cleanFormData.password);

      const loginRes = await fetch(`${API_URL}/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: loginPayload,
      });

      if (!loginRes.ok) throw new Error('Login after registration failed');
      
      const loginData = await loginRes.json();
      localStorage.setItem('token', loginData.access_token);
      
      const userRes = await fetch(`${API_URL}/users/me`, {
        headers: { Authorization: `Bearer ${loginData.access_token}` }
      });
      
      if (!userRes.ok) throw new Error('Failed to fetch user info');
      const userData = await userRes.json();
      login(userData);

      const redirectPath = localStorage.getItem('redirectAfter');
      if (redirectPath) {
        localStorage.removeItem('redirectAfter');
        navigate(redirectPath);
      } else {
        navigate('/passes');
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setFieldErrors((prev) => ({ ...prev, [e.target.name]: undefined }));
  };

  return (
    <div className="min-h-screen w-full flex bg-[#050505] text-white font-sans overflow-hidden">
      
      {/* --- LEFT SIDE: REGISTER FORM --- */}
      <div className="w-full md:w-[50%] lg:w-[45%] flex flex-col justify-center py-12 px-8 sm:px-12 md:px-20 lg:px-24 z-10 relative overflow-y-auto">
        
        <div className="max-w-md w-full mx-auto">
          <div className="mb-8 mt-8">
            <h1 className="text-4xl md:text-5xl font-medium tracking-tight mb-3 text-white">Crear cuenta</h1>
            <p className="text-gray-400 text-base font-light">Regístrate una sola vez para acceder a todos los eventos.</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Social Login Buttons (Dummy)
            <div className="grid grid-cols-2 gap-4 mb-6">
              <button type="button" className="flex items-center justify-center gap-3 py-3 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-sm font-medium text-white/80">
                <FaGoogle className="text-lg" /> Google
              </button>
              <button type="button" className="flex items-center justify-center gap-3 py-3 rounded-xl border border-white/10 hover:bg-white/5 transition-colors text-sm font-medium text-white/80">
                <FaApple className="text-xl" /> Apple
              </button>
            </div>

            <div className="relative flex py-2 items-center">
              <div className="flex-grow border-t border-white/10"></div>
              <span className="flex-shrink mx-4 text-gray-500 text-xs uppercase tracking-wider">O rellena tus datos</span>
              <div className="flex-grow border-t border-white/10"></div>
            </div>

            {/* --- FORM FIELDS --- */}
            
            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Nombre de Usuario</label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                className={`w-full px-5 py-4 rounded-xl bg-white/[0.03] border ${fieldErrors.username ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all`}
                placeholder="Como te llamarán tus amigos"
                required
              />
              {fieldErrors.username && <p className="text-red-400 text-xs mt-1 ml-1">{fieldErrors.username}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Nombre Completo</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-5 py-4 rounded-xl bg-white/[0.03] border ${fieldErrors.name ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all`}
                placeholder="Ej. Juan Pérez"
                required
                autoComplete="name"
              />
              {fieldErrors.name && <p className="text-red-400 text-xs mt-1 ml-1">{fieldErrors.name}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-5 py-4 rounded-xl bg-white/[0.03] border ${fieldErrors.email ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all`}
                placeholder="ejemplo@correo.com"
                required
                autoComplete="off"
              />
              {fieldErrors.email && <p className="text-red-400 text-xs mt-1 ml-1">{fieldErrors.email}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">DNI / Documento</label>
              <input
                type="text"
                name="dni"
                value={formData.dni}
                onChange={handleChange}
                className={`w-full px-5 py-4 rounded-xl bg-white/[0.03] border ${fieldErrors.dni ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all`}
                placeholder="Para verificar tu identidad en puerta"
                required
                autoComplete="off"
              />
              {fieldErrors.dni && <p className="text-red-400 text-xs mt-1 ml-1">{fieldErrors.dni}</p>}
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Contraseña</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-5 py-4 rounded-xl bg-white/[0.03] border ${fieldErrors.password ? 'border-red-500/50' : 'border-white/10'} text-white placeholder-gray-600 focus:outline-none focus:border-white/40 focus:bg-white/[0.05] transition-all pr-12`}
                  placeholder="Mínimo 6 caracteres"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                </button>
              </div>
              {fieldErrors.password && <p className="text-red-400 text-xs mt-1 ml-1">{fieldErrors.password}</p>}
            </div>

            {/* --- 3. CHECKBOX DE TÉRMINOS Y CONDICIONES --- */}
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer group select-none">
                <div className="relative flex items-center pt-0.5">
                  <input 
                    type="checkbox" 
                    className="peer appearance-none h-4 w-4 border border-white/20 rounded bg-transparent checked:bg-white checked:border-white transition-all cursor-pointer"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                  />
                  {/* Custom Checkmark */}
                  <svg className="absolute w-3 h-3 text-black pointer-events-none hidden peer-checked:block left-0.5 top-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors leading-tight">
                    He leído y acepto los <Link to="/terminos" target="_blank" className="text-white underline hover:text-gray-200">Términos y condiciones</Link> y la Política de privacidad.
                </span>
              </label>
            </div>

            {/* General Error Message */}
            {error && (
              <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm text-center animate-pulse mt-4">
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-4 mt-6 bg-white text-black font-semibold rounded-full hover:scale-[1.01] active:scale-[0.98] transition-all shadow-[0_0_20px_-5px_rgba(255,255,255,0.2)]"
            >
              Registrarme
            </button>

            <div className="text-center mt-8 pb-4">
              <span className="text-gray-500 text-sm">¿Ya tienes cuenta? </span>
              <Link to="/login" className="text-white hover:underline decoration-white/50 underline-offset-4 font-medium text-sm ml-1">
                Inicia sesión aquí
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* --- RIGHT SIDE: ANIMATED SPHERE --- */}
      <div className="hidden md:block w-[50%] lg:w-[55%] relative bg-black overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
             <img 
              src="/gif/spherefig.gif" 
              alt="Artistic Sphere" 
              className="w-full h-full object-cover will-change-transform opacity-90" 
            />
        </div>
        
        {/* Optional Quote */}
        <div className="absolute bottom-12 left-12 max-w-sm z-10 pointer-events-none">
            <p className="text-white/60 text-sm font-serif italic">"Join the club."</p>
        </div>
      </div>

    </div>
  );
}